import React, { useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import StatusBadge from '../shared/StatusBadge';
import { DollarSign, Clock, AlertCircle, Target } from 'lucide-react';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };

export default function Dashboard() {
  const { dashboardStats, fetchDashboard, setCurrentSection, user } = useApp();
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (!dashboardStats) return <div style={{ padding: '2rem', color: '#94a3b8' }}>Loading...</div>;
  const isStaff = user?.role === 'staff' || dashboardStats.mode === 'staff';

  if (isStaff) {
    const staffStats = [
      { label: 'Assigned Projects', value: dashboardStats.assignedProjects || 0, icon: Target, color: '#22d3ee' },
      { label: 'Avg Progress', value: `${dashboardStats.avgProgress || 0}%`, icon: Clock, color: '#34d399' },
      { label: 'Unread Messages', value: dashboardStats.unreadMessages || 0, icon: AlertCircle, color: '#fbbf24' },
    ];

    return (
      <div style={{ padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>Dashboard</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {staffStats.map((s, i) => (
            <div key={i} style={{ ...card, padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{s.label}</span>
                <s.icon style={{ width: '1.25rem', height: '1.25rem', color: s.color }} />
              </div>
              <p style={{ fontSize: '2rem', fontWeight: 700, color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
        <div style={{ ...card, padding: '1.5rem' }}>
          <h2 style={{ fontWeight: 600, marginBottom: '1rem' }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[['My Projects', 'projects', '#34d399'], ['Team Messages', 'messages', '#22d3ee']].map(([label, section, color], i) => (
              <button key={i} onClick={() => setCurrentSection(section)} style={{ padding: '0.75rem 1rem', background: `${color}15`, color, borderRadius: '0.5rem', textAlign: 'left' }}>{label}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Total Revenue', value: `$${(dashboardStats.totalRevenue || 0).toLocaleString()}`, icon: DollarSign, color: '#34d399' },
    { label: 'Pending', value: `$${(dashboardStats.pendingRevenue || 0).toLocaleString()}`, icon: Clock, color: '#fbbf24' },
    { label: 'Overdue', value: `$${(dashboardStats.overdueAmount || 0).toLocaleString()}`, icon: AlertCircle, color: '#f87171' },
    { label: 'New Leads', value: dashboardStats.newLeads || 0, icon: Target, color: '#22d3ee' },
  ];

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>Dashboard</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ ...card, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{s.label}</span>
              <s.icon style={{ width: '1.25rem', height: '1.25rem', color: s.color }} />
            </div>
            <p style={{ fontSize: '2rem', fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="dashboard-grid" style={{ gap: '1.5rem' }}>
        <div style={{ ...card, padding: '1.5rem' }}>
          <h2 style={{ fontWeight: 600, marginBottom: '1rem' }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[['Create Invoice', 'invoices', '#34d399'], ['Add Client', 'clients', '#22d3ee'], ['Find Leads', 'leads', '#a78bfa']].map(([label, section, color], i) => (
              <button key={i} onClick={() => setCurrentSection(section)} style={{ padding: '0.75rem 1rem', background: `${color}15`, color, borderRadius: '0.5rem', textAlign: 'left' }}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ ...card, padding: '1.5rem' }}>
          <h2 style={{ fontWeight: 600, marginBottom: '1rem' }}>Recent Invoices</h2>
          {(dashboardStats.recentInvoices || []).slice(0, 5).map(inv => (
            <div key={inv._id} className="recent-invoice-item" style={{ padding: '0.75rem', background: 'rgba(15,23,42,0.3)', borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
              <div className="invoice-meta"><p className="client-name" style={{ fontWeight: 500 }}>{inv.clientName}</p><p className="invoice-number" style={{ fontSize: '0.875rem', color: '#64748b' }}>{inv.invoiceNumber}</p></div>
              <div className="invoice-right" style={{ textAlign: 'right' }}><p style={{ fontWeight: 600 }}>${inv.total.toLocaleString()}</p><StatusBadge status={inv.status} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
